import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { GapChart } from "@/components/GapChart";
import { IncomeBars } from "@/components/NetWorthDonut";
import { lifeGapRows } from "@/components/KpiStrip";
import { api } from "@/lib/api";
import { computeSynopsis } from "@/lib/calc";
import { money, pct } from "@/lib/format";
import { ASSET_ROWS, FINAL_ROWS, LIAB_ROWS } from "@/lib/schema";

const KV = ({ k, v, strong, testId }) => (
  <div className={`flex justify-between py-0.5 border-b border-slate-100 text-[12px] ${strong ? "font-bold text-slate-900" : ""}`}>
    <span className={strong ? "" : "text-slate-600"}>{k}</span><span className="font-mono" data-testid={testId}>{v}</span>
  </div>
);
const H = ({ children }) => <h2 className="report-band">{children}</h2>;

export default function ReportPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [client, setClient] = useState(null);
  useEffect(() => { api.get(id).then(setClient).catch(() => nav("/clients")); }, [id, nav]);
  const s = useMemo(() => (client ? computeSynopsis(client.data) : null), [client]);
  if (!s) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0072C6]" /></div>;

  const { d, exp, ov, ins, ret } = s;
  const p = d.profile;
  const cur = p.currency;
  const m = (x) => money(x, cur);
  const people = [["client", p.name || "Client"], ...(ins.hasSpouse ? [["spouse", p.spouse.name || "Spouse"]] : [])];

  return (
    <div className="min-h-screen report-bg">
      <AppHeader>
        <Button variant="ghost" size="sm" onClick={() => nav(`/clients/${id}`)} data-testid="report-back-button" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Edit client</Button>
        <Button size="sm" onClick={() => window.print()} data-testid="report-print-button" className="gap-1.5"><Printer className="h-4 w-4" /> Print / Save PDF</Button>
      </AppHeader>
      <article className="report-sheet" data-testid="report-sheet">
        <header className="flex items-center justify-between border-b-2 border-slate-900 pb-2">
          <img src="/experior-logo.svg" alt="Experior Financial Group" className="h-11" />
          <div className="text-right">
            <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900">CLIENT FINANCIAL NEEDS SYNOPSIS</h1>
            <p className="text-[11px] text-slate-500">Detailed worksheet · <span data-testid="report-client-name">{p.name}</span> · {p.meetingDate}</p>
          </div>
        </header>

        <H>CLIENT & FAMILY</H>
        <div className="grid grid-cols-2 gap-x-8">
          <KV k="Client" v={`${p.name} · age ${s.age}`} /><KV k="Spouse" v={p.spouse.name ? `${p.spouse.name} · age ${s.spouseAge}` : "-"} />
          <KV k="Retirement age / life expectancy" v={`${p.retirementAge} / ${p.lifeExpectancy}`} /><KV k="Children / other dependants" v={`${p.children.length} / ${p.otherDependants || 0}`} />
          <KV k="Advisor" v={p.advisorName} /><KV k="Location" v={[p.city, p.province].filter(Boolean).join(", ") || "-"} />
        </div>

        <H>FINANCIAL OVERVIEW</H>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <KV k="Client income" v={m(d.financial.income.clientAnnual)} /><KV k="Spouse income" v={m(d.financial.income.spouseAnnual)} /><KV k="Other income" v={m(d.financial.income.otherAnnual)} />
            <KV k="Take-home income / mo" v={ov.takeHomeMonthly ? m(ov.takeHomeMonthly) : "Not entered"} /><KV k="Monthly expenses" v={m(exp.totalMonthly)} /><KV k="Debt payments / mo" v={m(ov.debtPayments)} />
            <KV k="Monthly cash flow" v={ov.cashFlow === null ? "Not entered" : m(ov.cashFlow)} strong />
          </div>
          <div>
            {ASSET_ROWS.map(([k, l]) => <KV key={k} k={`${l} (${d.financial.assets[k]?.use || "-"})`} v={m(d.financial.assets[k]?.amount)} />)}
            <KV k="Investments" v={m(ov.investmentsTotal)} /><KV k="Total assets" v={m(ov.totalAssets)} strong />
          </div>
          <div>
            {LIAB_ROWS.map(([k, l]) => <KV key={k} k={l} v={m(d.financial.liabilities[k]?.amount)} />)}
            <KV k="Total liabilities" v={m(ov.totalLiabilities)} strong /><KV k="Net worth" v={m(ov.netWorth)} strong testId="report-net-worth" />
          </div>
        </div>
        <table className="report-table mt-2" data-testid="report-expenses-table"><thead><tr><th>Expenses</th><th>Current / Mo</th><th>Current / Yr</th><th>Share</th><th>Retirement / Mo</th><th>Retirement / Yr</th></tr></thead><tbody>
          {exp.byCategory.map((c) => <tr key={c.category}><td>{c.category}</td><td>{m(c.cur)}</td><td>{m(c.curYear)}</td><td>{pct(c.share)}</td><td>{m(c.ret)}</td><td>{m(c.retYear)}</td></tr>)}
          <tr><td>Income taxes (annual)</td><td>-</td><td>{m(exp.taxesAnnual)}</td><td /><td>{m(exp.taxesRet)}</td><td>{m(exp.taxesRet * 12)}</td></tr>
          <tr className="font-bold"><td>Total</td><td>{m(exp.totalMonthly)}</td><td>{m(exp.totalYearly)}</td><td /><td>{m(exp.retMonthly)}</td><td>{m(exp.retYearly)}</td></tr>
        </tbody></table>

        <div className="avoid-break">
          <H>INVESTMENT & RETIREMENT</H>
          <table className="report-table"><thead><tr><th>Account</th><th>Owner</th><th>Value</th><th>Monthly</th><th>Return</th><th>Purpose</th><th>Retire</th><th>Value at {ret.retAge}</th><th>Monthly draw</th></tr></thead><tbody>
            {[...ret.accounts, ...ret.excluded].map((a) => <tr key={a.id}><td>{a.type}</td><td>{a.owner}</td><td>{m(a.value)}</td><td>{m(a.monthly)}</td><td>{a.ror}%</td><td>{a.purpose}</td><td>{a.retirement}</td><td>{a.included ? m(a.fv) : "-"}</td><td>{a.included ? m(a.draw) : "-"}</td></tr>)}
          </tbody></table>
          <div className="grid grid-cols-2 gap-6 mt-2">
            <div>
              <KV k="CPP" v={m(ret.cpp)} /><KV k="OAS" v={m(ret.oas)} /><KV k="Employer pension (DB)" v={m(ret.employerPension)} /><KV k="Other income" v={m(ret.otherMonthly)} />
              {ret.withSpouse && <><KV k={`${p.name || "Client"} projected income`} v={m(ret.client.expected)} /><KV k={`${p.spouse.name || "Spouse"} projected income (CPP ${m(ret.spouse.cpp)}, OAS ${m(ret.spouse.oas)})`} v={m(ret.spouse.expected)} /></>}
              <KV k="Total projected monthly income" v={m(ret.expectedMonthly)} strong /><KV k="Desired monthly income" v={m(ret.monthlyGoal)} />
              <KV k={ret.monthlySurplus < 0 ? "MONTHLY SHORTFALL" : "MONTHLY SURPLUS"} v={m(ret.monthlySurplus)} strong />
            </div>
            <div>
              <KV k="Existing retirement savings" v={m(ret.existingSavings)} /><KV k="Total projected savings" v={m(ret.totalProjected)} />
              <KV k="Financial Independence Number (FIN)" v={m(ret.fin)} testId="report-fin" /><KV k="Funding shortfall" v={m(ret.savingsNeeded ?? "None")} />
              <KV k={`Annual contribution (at ${d.retirement.targetRor}%)`} v={m(ret.annualContribution ?? "Not required")} />
              <KV k="ADDITIONAL MONTHLY SAVINGS" v={m(ret.monthlyContribution ?? "Not required")} strong />
              <div className="mt-1"><IncomeBars sources={ret.sources} goal={ret.monthlyGoal} currency={cur} /></div>
            </div>
          </div>
        </div>

        <div className="avoid-break">
          <H>INSURANCE NEEDS</H>
          <div className="grid grid-cols-5 gap-6">
            <table className="report-table col-span-3"><thead><tr><th />{people.map(([k, n]) => <th key={k}>{n}</th>)}</tr></thead><tbody>
              {[["Income replacement", (r) => m(r.incomeRepl ?? "Not used")], ["Family & dependant needs", (r) => m(r.familyNeeds ?? "Not used")], ["Mortgage & liabilities", (r) => m(r.liabilities)],
                ["Other obligations", (r) => m(r.obligations)], ["Total requirement", (r) => m(r.totalNeed)], ["Existing life coverage", (r) => m(r.existingLife)], ["Assets available", (r) => m(r.availableAssets)],
                ["Additional life coverage", (r) => m(r.lifeAdditional)], ["Disability need / existing / gap", (r) => `${m(r.diRequired)} / ${m(r.diExisting)} / ${m(r.diShortfall)}`],
                ["Critical illness need / existing / gap", (r) => `${m(r.ciRequired)} / ${m(r.ciExisting)} / ${m(r.ciAdditional)}`]].map(([l, f]) => (
                <tr key={l}><td>{l}</td>{people.map(([k]) => <td key={k}>{f(ins[k])}</td>)}</tr>
              ))}
            </tbody></table>
            <div className="col-span-2">
              <GapChart rows={lifeGapRows(s)} currency={cur} height={180} fixedWidth={270} />
              <p className="text-[10.5px] text-slate-500 mt-1">Final expenses: {FINAL_ROWS.map(([k, l]) => `${l} ${m(d.insurance.finals[k]?.amount)}`).join(" · ")}</p>
            </div>
          </div>
        </div>

        {s.plan.rows.length > 0 && (
          <div className="avoid-break">
            <H>RECOMMENDED PLAN</H>
            <table className="report-table" data-testid="report-plan-table"><thead><tr><th>Need</th><th>For</th><th>Gap</th><th>Product</th><th>Monthly premium</th></tr></thead><tbody>
              {s.plan.rows.map((x) => <tr key={x.id}><td>{x.need}</td><td>{x.person}</td><td>{typeof x.gap === "number" ? m(x.gap) : x.gap}</td><td>{x.product}</td><td>{m(x.premium)}</td></tr>)}
              <tr className="font-bold"><td colSpan={4}>Total monthly premium</td><td>{m(s.plan.totalPremium)}</td></tr>
            </tbody></table>
          </div>
        )}

        <div className="avoid-break">
          <H>ACKNOWLEDGEMENT</H>
          <div className="border border-slate-300 rounded min-h-[50px] p-2 text-xs whitespace-pre-wrap" data-testid="report-remarks">{p.remarks}</div>
          <div className="grid grid-cols-4 gap-6 mt-8 text-[11px] text-slate-600">
            <div className="border-t border-slate-500 pt-1">Client signature</div><div className="border-t border-slate-500 pt-1">Date</div>
            <div className="border-t border-slate-500 pt-1">Advisor: <b className="text-slate-900">{p.advisorName}</b></div><div className="border-t border-slate-500 pt-1">Date</div>
          </div>
        </div>
      </article>
    </div>
  );
}
